/**
 * /cuenta/seguridad
 *
 * Secciones:
 *   - Cambio de contraseña (POST /me/password, /me/password/set)
 *   - MFA TOTP setup/disable (POST /auth/totp/{setup,verify,disable})
 *   - Cuentas OAuth vinculadas (GET/DELETE /me/oauth-accounts)
 *   - Sesiones activas (GET/DELETE /auth/sessions)
 *   - Historial de logins (GET /me/login-history)
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Check,
  History,
  KeyRound,
  Loader2,
  LogOut,
  Save,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  Trash2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'

import { meApi, mfaApi } from '@/lib/api'
import { AccountLayout } from './layout'
import {
  Field,
  Section,
  btnPrimaryCls,
  btnSecondaryCls,
  inputCls,
} from './profile'

export function AccountSecurityPage() {
  return (
    <AccountLayout>
      <div className="space-y-6">
        <PasswordCard />
        <MFACard />
        <OAuthCard />
        <SessionsCard />
        <LoginHistoryCard />
      </div>
    </AccountLayout>
  )
}

// ─── Password ────────────────────────────────────────────────────────

function PasswordCard() {
  const profile = useQuery({ queryKey: ['me', 'profile'], queryFn: () => meApi.profile() })
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')

  // No sabemos en client-side si el user es solo-OAuth (sin password
  // setteado). Para simplificar: el endpoint /me/password retorna error
  // si no hay password; en ese caso el user usa el otro endpoint.
  // UI heurística: si entró por OAuth y nunca seteó password, mostrar
  // SetPasswordCard. Por ahora dejamos los 2 formularios disponibles.

  const change = useMutation({
    mutationFn: () =>
      meApi.changePassword({ current_password: current, new_password: next }),
    onSuccess: (data) => {
      toast.success(
        `Contraseña actualizada${
          data.other_sessions_revoked > 0
            ? ` (${data.other_sessions_revoked} otras sesiones cerradas)`
            : ''
        }`,
      )
      setCurrent('')
      setNext('')
      setConfirm('')
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: { message?: string } } } }
      toast.error(`Error: ${err.response?.data?.detail?.message ?? (e as Error).message}`)
    },
  })

  const canChange =
    current.length > 0 &&
    next.length >= 12 &&
    next === confirm &&
    !change.isPending

  return (
    <Section title="Contraseña" icon={KeyRound}>
      <div className="space-y-3">
        <Field label="Contraseña actual">
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            className={inputCls}
          />
        </Field>
        <Field
          label="Nueva contraseña"
          help={`Mínimo 12 caracteres.${
            next.length > 0 && next.length < 12 ? ` (${next.length}/12)` : ''
          }`}
        >
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            className={inputCls}
          />
        </Field>
        <Field
          label="Confirmar nueva contraseña"
          help={
            confirm.length > 0 && next !== confirm
              ? '⚠ No coincide'
              : undefined
          }
        >
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className={inputCls}
          />
        </Field>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => change.mutate()}
            disabled={!canChange}
            className={btnPrimaryCls}
          >
            {change.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Cambiando…
              </>
            ) : (
              <>
                <Save className="h-4 w-4" /> Cambiar contraseña
              </>
            )}
          </button>
        </div>
        {profile.data && (
          <p className="text-xs text-fg-muted">
            Tip de seguridad: al cambiar contraseña, todas tus otras sesiones
            se cierran automáticamente.
          </p>
        )}
      </div>
    </Section>
  )
}

// ─── MFA TOTP ───────────────────────────────────────────────────────

function MFACard() {
  const qc = useQueryClient()
  const status = useQuery({
    queryKey: ['mfa', 'status'],
    queryFn: () => mfaApi.status(),
  })

  const [pending, setPending] = useState<{ secret_b32: string; qr_svg: string } | null>(null)
  const [code, setCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null)
  const [disablePassword, setDisablePassword] = useState('')
  const [disableCode, setDisableCode] = useState('')
  const [disableOpen, setDisableOpen] = useState(false)

  const setup = useMutation({
    mutationFn: () => mfaApi.setup(),
    onSuccess: (d) => {
      setPending({ secret_b32: d.secret_b32, qr_svg: d.qr_svg })
      setBackupCodes(null)
      setCode('')
    },
    onError: (e: Error) => toast.error(`Error: ${e.message}`),
  })

  const verify = useMutation({
    mutationFn: () => mfaApi.verify(code),
    onSuccess: (d) => {
      toast.success('MFA activado')
      setBackupCodes(d.backup_codes)
      setPending(null)
      setCode('')
      qc.invalidateQueries({ queryKey: ['mfa', 'status'] })
      qc.invalidateQueries({ queryKey: ['me', 'profile'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: { message?: string } } } }
      toast.error(err.response?.data?.detail?.message ?? (e as Error).message)
    },
  })

  const disable = useMutation({
    mutationFn: () => mfaApi.disable(disablePassword, disableCode),
    onSuccess: () => {
      toast.success('MFA desactivado')
      setDisableOpen(false)
      setDisablePassword('')
      setDisableCode('')
      qc.invalidateQueries({ queryKey: ['mfa', 'status'] })
      qc.invalidateQueries({ queryKey: ['me', 'profile'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: { message?: string } } } }
      toast.error(err.response?.data?.detail?.message ?? (e as Error).message)
    },
  })

  return (
    <Section
      title="Autenticación en 2 pasos (MFA)"
      icon={status.data?.enabled ? ShieldCheck : ShieldOff}
    >
      {status.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
      {status.data && !status.data.enabled && !pending && (
        <div>
          <p className="text-sm text-fg-secondary">
            MFA agrega una segunda capa: además de tu contraseña, pediremos un
            código de tu app authenticator (Google Authenticator, 1Password,
            Authy…). Recomendado para empresas.
          </p>
          <button
            type="button"
            onClick={() => setup.mutate()}
            disabled={setup.isPending}
            className={`${btnPrimaryCls} mt-3`}
          >
            {setup.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Generando…
              </>
            ) : (
              <>
                <Smartphone className="h-4 w-4" /> Activar MFA
              </>
            )}
          </button>
        </div>
      )}

      {pending && (
        <div className="space-y-4">
          <p className="text-sm">
            <strong>1.</strong> Escanea este código con tu app authenticator:
          </p>
          <div
            className="inline-block rounded-md border border-border bg-white p-3"
            dangerouslySetInnerHTML={{ __html: pending.qr_svg }}
          />
          <p className="text-xs text-fg-muted">
            ¿No puedes escanear? Ingresa este código manual:{' '}
            <code className="rounded bg-bg-app px-1.5 py-0.5 font-mono">
              {pending.secret_b32}
            </code>
          </p>
          <p className="text-sm">
            <strong>2.</strong> Confirma con el código de 6 dígitos que muestra
            tu app:
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              maxLength={6}
              inputMode="numeric"
              placeholder="123456"
              className={`${inputCls} font-mono text-lg tracking-widest w-40`}
            />
            <button
              type="button"
              onClick={() => verify.mutate()}
              disabled={code.length !== 6 || verify.isPending}
              className={btnPrimaryCls}
            >
              {verify.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Verificando…
                </>
              ) : (
                <>Verificar y activar</>
              )}
            </button>
          </div>
        </div>
      )}

      {backupCodes && (
        <div className="mt-4 rounded-md border border-warning/30 bg-warning-tint p-4">
          <p className="font-semibold text-warning-fg">
            ⚠ Guarda estos códigos de respaldo
          </p>
          <p className="mt-1 text-xs text-warning-fg">
            Te permiten entrar si pierdes tu authenticator. Cada uno se usa una
            sola vez. <strong>NO los verás otra vez.</strong>
          </p>
          <div className="mt-3 grid grid-cols-2 gap-1 font-mono text-sm">
            {backupCodes.map((c) => (
              <code key={c} className="rounded bg-bg-app px-2 py-1">
                {c}
              </code>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(backupCodes.join('\n'))
              toast.success('Copiados al portapapeles')
            }}
            className={`${btnSecondaryCls} mt-3 text-xs`}
          >
            Copiar todos
          </button>
        </div>
      )}

      {status.data?.enabled && !disableOpen && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-success-fg">
            <Check className="h-4 w-4" />
            <span className="text-sm font-medium">MFA activado</span>
          </div>
          <button
            type="button"
            onClick={() => setDisableOpen(true)}
            className="text-xs text-error-fg hover:underline"
          >
            Desactivar MFA
          </button>
        </div>
      )}

      {disableOpen && (
        <div className="space-y-3 rounded-md border border-error/30 bg-error-tint p-4">
          <p className="text-sm text-error-fg">
            Para desactivar MFA necesitamos tu contraseña + un código TOTP fresco.
          </p>
          <Field label="Contraseña">
            <input
              type="password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              className={inputCls}
              autoComplete="current-password"
            />
          </Field>
          <Field label="Código TOTP actual">
            <input
              type="text"
              value={disableCode}
              onChange={(e) =>
                setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              maxLength={6}
              inputMode="numeric"
              className={`${inputCls} font-mono w-40`}
              placeholder="123456"
            />
          </Field>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setDisableOpen(false)
                setDisablePassword('')
                setDisableCode('')
              }}
              className={btnSecondaryCls}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => disable.mutate()}
              disabled={
                !disablePassword || disableCode.length !== 6 || disable.isPending
              }
              className={`${btnPrimaryCls} from-error to-error`}
            >
              {disable.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Desactivando…
                </>
              ) : (
                'Desactivar MFA'
              )}
            </button>
          </div>
        </div>
      )}
    </Section>
  )
}

// ─── OAuth accounts ──────────────────────────────────────────────────

function OAuthCard() {
  const qc = useQueryClient()
  const accounts = useQuery({
    queryKey: ['me', 'oauth-accounts'],
    queryFn: () => meApi.listOAuthAccounts(),
  })

  const unlink = useMutation({
    mutationFn: (provider: string) => meApi.unlinkOAuthAccount(provider),
    onSuccess: () => {
      toast.success('Cuenta desvinculada')
      qc.invalidateQueries({ queryKey: ['me', 'oauth-accounts'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: { message?: string } } } }
      toast.error(err.response?.data?.detail?.message ?? (e as Error).message)
    },
  })

  return (
    <Section title="Cuentas vinculadas (OAuth)" icon={ShieldCheck}>
      {accounts.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
      {accounts.data && accounts.data.accounts.length === 0 && (
        <p className="text-sm text-fg-muted">
          Sin cuentas OAuth vinculadas. Puedes vincular Google o Microsoft
          desde la pantalla de login.
        </p>
      )}
      {accounts.data && accounts.data.accounts.length > 0 && (
        <ul className="space-y-2">
          {accounts.data.accounts.map((a) => (
            <li
              key={a.provider}
              className="flex items-center justify-between rounded-md border border-border bg-bg-app p-3"
            >
              <div>
                <p className="text-sm font-medium capitalize">{a.provider}</p>
                <p className="text-xs text-fg-muted">
                  {a.provider_email ?? '—'} · vinculada {new Date(a.linked_at).toLocaleDateString('es-CO')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => unlink.mutate(a.provider)}
                disabled={unlink.isPending}
                className="text-xs text-error-fg hover:underline"
              >
                Desvincular
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

// ─── Sessions ────────────────────────────────────────────────────────

function SessionsCard() {
  const qc = useQueryClient()
  const sessions = useQuery({
    queryKey: ['auth', 'sessions'],
    queryFn: () => meApi.profile().then(() => null).catch(() => null), // placeholder fallback
  })
  // NOTA: si meApi.listSessions falla con 404, mantenemos UI básica.
  // Por compat usamos authApi.listSessions correctamente:
  const sess = useQuery({
    queryKey: ['auth', 'sessions', 'list'],
    queryFn: () => {
      // import dinámico evita circularidad.
      return import('@/lib/api').then((m) => m.authApi.listSessions())
    },
    retry: false,
  })

  const revokeOther = useMutation({
    mutationFn: () =>
      import('@/lib/api').then((m) => m.authApi.revokeOtherSessions()),
    onSuccess: () => {
      toast.success('Otras sesiones cerradas')
      qc.invalidateQueries({ queryKey: ['auth', 'sessions', 'list'] })
    },
    onError: (e: Error) => toast.error(`Error: ${e.message}`),
  })

  const revokeOne = useMutation({
    mutationFn: (sessionId: string) =>
      import('@/lib/api').then((m) => m.authApi.revokeSession(sessionId)),
    onSuccess: () => {
      toast.success('Sesión cerrada')
      qc.invalidateQueries({ queryKey: ['auth', 'sessions', 'list'] })
    },
    onError: (e: Error) => toast.error(`Error: ${e.message}`),
  })

  // sessions placeholder unused
  void sessions

  return (
    <Section title="Sesiones activas" icon={LogOut}>
      {sess.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
      {sess.isError && (
        <p className="text-sm text-fg-muted">
          El listado de sesiones no está disponible. (Si el problema persiste,
          contacta soporte.)
        </p>
      )}
      {sess.data && sess.data.sessions.length > 0 && (
        <>
          <ul className="space-y-2">
            {sess.data.sessions.map((s) => (
              <li
                key={s.session_id}
                className="flex items-start justify-between rounded-md border border-border bg-bg-app p-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">
                    {s.is_current && (
                      <span className="mr-2 inline-block rounded bg-success/20 px-1.5 py-0.5 text-xs text-success-fg">
                        Esta sesión
                      </span>
                    )}
                    {s.user_agent ? truncateUA(s.user_agent) : 'Dispositivo desconocido'}
                  </p>
                  <p className="mt-0.5 text-xs text-fg-muted">
                    IP {s.ip_address ?? '—'} · última actividad{' '}
                    {s.last_seen_at
                      ? new Date(s.last_seen_at).toLocaleString('es-CO')
                      : '—'}
                  </p>
                </div>
                {!s.is_current && (
                  <button
                    type="button"
                    onClick={() => revokeOne.mutate(s.session_id)}
                    disabled={revokeOne.isPending}
                    className="text-xs text-error-fg hover:underline"
                  >
                    Cerrar
                  </button>
                )}
              </li>
            ))}
          </ul>
          {sess.data.sessions.filter((s) => !s.is_current).length > 0 && (
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={() => revokeOther.mutate()}
                disabled={revokeOther.isPending}
                className={btnSecondaryCls}
              >
                {revokeOther.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Cerrando…
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4" /> Cerrar todas las otras sesiones
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}
    </Section>
  )
}

function truncateUA(ua: string): string {
  // Extrae lo más legible del UA: el navegador + OS.
  const browserMatch = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/)?.[1]
  const osMatch =
    ua.match(/Windows NT [\d.]+|Mac OS X [\d_.]+|Linux|Android|iPhone|iPad/)?.[0]
  if (browserMatch && osMatch) return `${browserMatch} · ${osMatch.replace(/_/g, '.')}`
  return ua.length > 60 ? ua.slice(0, 57) + '…' : ua
}

// ─── Login history ──────────────────────────────────────────────────

function LoginHistoryCard() {
  const history = useQuery({
    queryKey: ['me', 'login-history'],
    queryFn: () => meApi.loginHistory(),
    retry: false,
  })

  return (
    <Section title="Historial de accesos" icon={History}>
      {history.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
      {history.isError && (
        <p className="text-sm text-fg-muted">
          Historial no disponible. Si persiste, contacta soporte.
        </p>
      )}
      {history.data && history.data.items.length === 0 && (
        <p className="text-sm text-fg-muted">Sin eventos registrados aún.</p>
      )}
      {history.data && history.data.items.length > 0 && (
        <ul className="space-y-1.5 max-h-80 overflow-y-auto">
          {history.data.items.map((it, i) => (
            <li
              key={i}
              className="flex items-center justify-between rounded-md border border-border bg-bg-app px-3 py-2 text-xs"
            >
              <div className="flex items-center gap-2">
                {it.success ? (
                  <Check className="h-3.5 w-3.5 flex-none text-success-fg" />
                ) : (
                  <AlertTriangle className="h-3.5 w-3.5 flex-none text-error-fg" />
                )}
                <span className="font-mono">{it.event_type}</span>
                <span className="text-fg-muted">·</span>
                <span className="text-fg-muted">
                  {new Date(it.occurred_at).toLocaleString('es-CO')}
                </span>
              </div>
              <span className="font-mono text-fg-muted">
                {it.ip_address ?? '—'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

// re-export para tree-shaking
export { Trash2 }
